<div class="camera-modal-overlay" *ngIf="cameraState.isVisible" (click)="close()" role="dialog" aria-modal="true"
    aria-labelledby="plate-scanner-title">
    <div class="camera-modal-container" (click)="$event.stopPropagation()">
        <div class="camera-modal-header">
            <div class="camera-header-info">
                <h3 id="plate-scanner-title">{{ 'plateScanner.title' | translate }}</h3>
                <span class="photos-counter" *ngIf="isPlateDetected" aria-live="polite">
                    {{ detectedPlate }} ({{ getConfidencePercentage() }}%)
                </span>
            </div>
            <button class="close-modal-btn" (click)="close()" [attr.aria-label]="'common.close' | translate">×</button>
        </div>

        <div class="camera-viewer">
            <!-- Camera Video -->
            <video #cameraVideo autoplay playsinline muted
                [style.display]="isCameraAvailable && !isImageLoaded ? 'block' : 'none'" class="camera-video">
            </video>

            <!-- Hidden Canvas for photo capture -->
            <canvas #cameraCanvas style=" display: none;"></canvas>

            <!-- Image Preview -->
            <div class="image-preview-container" *ngIf="isImageLoaded">
                <img [src]="imagePreview" alt="{{ 'plateScanner.imagePreview' | translate }}" class="camera-video" />
            </div>

            <!-- Placeholder / States -->
            <div class="camera-placeholder" *ngIf="!isCameraAvailable && !isImageLoaded">
                <!-- Loading State -->
                <div class="camera-loading" *ngIf="cameraState.isLoading">
                    <div class="spinner" aria-hidden="true"></div>
                    <p>{{ 'protocol_orders.loadingCamera' | translate }}</p>
                </div>

                <!-- Scanning State -->
                <div class="camera-loading" *ngIf="isScanning && !isImageLoaded">
                    <div class="spinner" aria-hidden="true"></div>
                    <p>{{ 'plateScanner.scanningPlate' | translate }}</p>
                </div>

                <!-- Error State -->
                <div class="camera-error" *ngIf="cameraState.error">
                    <p>{{ cameraState.error }}</p>
                    <button (click)="retryCamera()" class="btn btn-primary"
                        [attr.aria-label]="'protocol_orders.retryCamera' | translate">
                        {{ 'protocol_orders.retryCamera' | translate }}
                    </button>
                </div>

                <!-- Initial State -->
                <div *ngIf="!cameraState.isLoading && !cameraState.error && !isScanning" class="initial-state">
                    <div class="camera-icon" aria-hidden="true">
                        <img src="assets/icons/camera.svg" alt="" />
                    </div>
                    <p>{{ 'plateScanner.uploadHint' | translate }}</p>
                    <p class="camera-instructions">{{ 'plateScanner.uploadImage' | translate }}</p>
                </div>
            </div>

            <!-- Camera Switch Button -->
            <button *ngIf="isCameraAvailable && cameraState.hasMultipleCameras && !isImageLoaded"
                class="btn-switch-camera" (click)="switchCamera()" [disabled]="cameraState.isLoading"
                [attr.aria-label]="'protocol_orders.switchCamera' | translate">
                <img src="assets/icons/video-camera-travel.svg" alt="" aria-hidden="true" />
                {{ 'protocol_orders.switchCamera' | translate }}
            </button>

            <!-- Change Image Button -->
            <button *ngIf="isImageLoaded && !isScanning" class="btn-switch-camera" (click)="reset(); open()"
                [attr.aria-label]="'plateScanner.changeImage' | translate">
                <img src="assets/icons/video-camera-travel.svg" alt="" aria-hidden="true" />
                {{ 'plateScanner.changeImage' | translate }}
            </button>
        </div>

        <!-- Results Panel -->
        <div class="results-panel" *ngIf="isPlateDetected && !isScanning">
            <div class="detected-plate-card">
                <div class="plate-badge success" role="status" aria-live="polite">
                    <span class="plate-text">{{ detectedPlate }}</span>
                </div>
                <div class="confidence-info" *ngIf="confidence !== null">
                    <span class="confidence-label">{{ 'plateScanner.confidence' | translate }}:</span>
                    <span class="confidence-value" [ngClass]="{'high-confidence': isConfidenceHighEnough()}">
                        {{ getConfidencePercentage() }}%
                    </span>
                </div>
            </div>

            <!-- Candidates Section -->
            <div class="candidates-section" *ngIf="candidates.length > 1">
                <p class="candidates-title">{{ 'plateScanner.alternatives' | translate }}</p>
                <div class="candidates-list">
                    <button class="candidate-item" *ngFor="let candidate of candidates.slice(1, 4)"
                        (click)="useCandidate(candidate)"
                        [attr.aria-label]="'plateScanner.selectCandidate' | translate">
                        <span class="candidate-plate">{{ candidate.plate.toUpperCase() }}</span>
                        <span class="candidate-score">{{ (candidate.score * 100).toFixed(0) }}%</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- No Results Panel -->
        <div class="no-results-panel" *ngIf="!isPlateDetected && !isScanning && isImageLoaded">
            <div class="camera-error">
                <p>{{ 'plateScanner.noPlateFound' | translate }}</p>
                <button (click)="reset(); open()" class="btn btn-primary">
                    {{ 'plateScanner.takeNewPhoto' | translate }}
                </button>
            </div>
        </div>

        <!-- Control Buttons -->
        <div class="camera-modal-controls" role="group" [attr.aria-label]="'plateScanner.controls' | translate">
            <!-- Cancel/Skip Button -->
            <button class="btn-camera-action btn-skip" (click)="close()" *ngIf="!isPlateDetected"
                [attr.aria-label]="'common.cancel' | translate">
                <img src="assets/icons/x.svg" alt="" aria-hidden="true" />
                {{ 'common.cancel' | translate }}
            </button>

            <!-- Capture Button -->
            <button class="btn-camera-action btn-capture" (click)="capturePhoto()"
                [disabled]="!isCameraAvailable || cameraState.isLoading"
                *ngIf="isCameraAvailable && !isImageLoaded && !isPlateDetected"
                [attr.aria-label]="'protocol_orders.cameraModalCapture' | translate">
                <img src="assets/icons/photograph.svg" alt="" aria-hidden="true" />
                {{ 'protocol_orders.cameraModalCapture' | translate }}
            </button>

            <!-- File Upload Button -->
            <button class="btn-camera-action btn-capture" (click)="fileInput.click()" [disabled]="isScanning"
                *ngIf="!isCameraAvailable && !isImageLoaded && !isPlateDetected && !cameraState.isLoading"
                [attr.aria-label]="'plateScanner.uploadImage' | translate">
                <img src="assets/icons/photograph.svg" alt="" aria-hidden="true" />
                {{ 'plateScanner.uploadImage' | translate }}
            </button>

            <!-- Scan Button -->
            <button class="btn-camera-action btn-capture" (click)="scanPlate()"
                [disabled]="isScanning || !isImageLoaded" *ngIf="isImageLoaded && !isPlateDetected && !isScanning"
                [attr.aria-label]="'plateScanner.retryWithSameImage' | translate">
                <img src="assets/icons/camera.svg" alt="" aria-hidden="true" />
                {{ 'plateScanner.retryWithSameImage' | translate }}
            </button>

            <!-- Use Plate Button -->
            <button class="btn-camera-action btn-cancel" (click)="usePlate(detectedPlate!)"
                *ngIf="isPlateDetected && !isScanning" [attr.aria-label]="'plateScanner.usePlate' | translate">
                <img src="assets/icons/check.svg" alt="" aria-hidden="true" />
                {{ 'plateScanner.usePlate' | translate }}
            </button>

            <!-- Retry Button -->
            <button class="btn-camera-action btn-skip" (click)="reset(); open()" *ngIf="isPlateDetected && !isScanning"
                [attr.aria-label]="'plateScanner.retry' | translate">
                <img src="assets/icons/video-camera-travel.svg" alt="" aria-hidden="true" />
                {{ 'plateScanner.retry' | translate }}
            </button>
        </div>

        <!-- Hidden File Input -->
        <input #fileInput type="file" accept="image/*" capture="environment" style="display: none;"
            (change)="onFileSelected($event)" [disabled]="isScanning"
            [attr.aria-label]="'plateScanner.selectImage' | translate" />
    </div>
</div>